import { useEffect, useState } from "react";
import { Camera, X, Send, ClipboardCheck, ChevronLeft, Search } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { SignaturePad } from "../SignaturePad";
import { SearchSelect } from "../SearchSelect";

const ipt = "w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white";

function Thumb({ file }) {
  const [src, setSrc] = useState("");
  useEffect(() => { const u = URL.createObjectURL(file); setSrc(u); return () => URL.revokeObjectURL(u); }, [file]);
  return src ? <img src={src} alt="" className="h-full w-full object-cover" /> : null;
}

function Field({ f, value, onChange, photos, setPhotos }) {
  const label = <div className="text-xs font-semibold text-slate-600 mb-1">{f.label}{f.required && " *"}</div>;
  if (f.type === "signature") return <div>{label}<SignaturePad testid={`form-signature-${f.id}`} onChange={(file) => setPhotos({ ...photos, [f.id]: file ? [file] : [] })} /></div>;
  if (f.type === "photo") {
    const list = photos[f.id] || [];
    return (
      <div>{label}
        <div className="flex flex-wrap gap-2">
          {list.map((p, i) => (
            <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-slate-200">
              <Thumb file={p} />
              <button type="button" onClick={() => setPhotos({ ...photos, [f.id]: list.filter((_, j) => j !== i) })} className="absolute top-0.5 right-0.5 bg-slate-900/70 rounded-full p-0.5 text-white"><X className="h-3 w-3" /></button>
            </div>
          ))}
          {list.length < 3 && (
            <label className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 grid place-items-center text-slate-500 cursor-pointer" data-testid={`form-photo-${f.id}`}>
              <Camera className="h-5 w-5" />
              <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" multiple className="hidden" data-testid={`form-photo-input-${f.id}`}
                onChange={(e) => { setPhotos({ ...photos, [f.id]: [...list, ...Array.from(e.target.files)].slice(0, 3) }); e.target.value = ""; }} />
            </label>
          )}
        </div>
      </div>
    );
  }
  const common = { "data-testid": `form-field-${f.id}`, required: f.required, value: value || "", onChange: (e) => onChange(e.target.value), className: ipt };
  if (f.type === "longtext") return <label className="block">{label}<textarea rows={4} {...common} /></label>;
  return <label className="block">{label}<input type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "time" ? "time" : "text"} step={f.type === "number" ? "any" : undefined} {...common} /></label>;
}

function FillForm({ tpl, onDone, onBack }) {
  const [siteId, setSiteId] = useState(tpl.sites[0]?.id || "");
  const [vals, setVals] = useState({});
  const [photos, setPhotos] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const catUrgent = !!tpl.category?.urgent;
  const [urgent, setUrgent] = useState(catUrgent);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      const pf = [];
      fd.append("template_id", tpl.id); fd.append("site_id", siteId); fd.append("values", JSON.stringify(vals)); fd.append("urgent", urgent ? "1" : "0");
      Object.entries(photos).forEach(([fid, list]) => list.forEach((p) => { fd.append("photos", p); pf.push(fid); }));
      fd.append("photo_fields", JSON.stringify(pf));
      await http.post("/agent-app/form-reports", fd);
      onDone();
    } catch (er) { setErr(formatApiError(er)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="agent-form-report">
      <button type="button" data-testid="form-back" onClick={onBack} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500"><ChevronLeft className="h-3.5 w-3.5" />Retour</button>
      <h2 className="font-display text-lg font-extrabold">{tpl.name}</h2>
      {tpl.description && <p className="text-xs text-slate-500">{tpl.description}</p>}
      {tpl.sites.length > 1 && (
        <SearchSelect data-testid="form-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} className={ipt}>
          {tpl.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </SearchSelect>
      )}
      {tpl.fields.map((f) => <Field key={f.id} f={f} value={vals[f.id]} onChange={(v) => { setErr(""); setVals({ ...vals, [f.id]: v }); }} photos={photos} setPhotos={(p) => { setErr(""); setPhotos(p); }} />)}
      <label className="flex items-center gap-2 text-sm font-semibold text-rose-700 cursor-pointer" data-testid="form-urgent-label">
        <input type="checkbox" checked={urgent} disabled={catUrgent} onChange={(e) => setUrgent(e.target.checked)} data-testid="form-urgent" />
        {catUrgent ? "Catégorie urgente : vos responsables seront alertés immédiatement" : "Incident urgent : alerter immédiatement mes responsables"}
      </label>
      {err && <div data-testid="form-error" className="text-sm rounded-md px-3 py-2 bg-rose-50 text-rose-700">{err}</div>}
      <button data-testid="form-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 text-white font-bold rounded-lg py-3 disabled:opacity-60"><Send className="h-4 w-4" />{busy ? "Envoi…" : "Envoyer"}</button>
    </form>
  );
}

export function SiteForms({ onSent }) {
  const [tpls, setTpls] = useState([]);
  const [open, setOpen] = useState(null);
  const [ok, setOk] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { http.get("/agent-app/report-templates").then((r) => setTpls(r.data)).catch(() => {}); }, []);
  if (open) return <FillForm tpl={open} onBack={() => setOpen(null)} onDone={() => { setOk(`« ${open.name} » envoyé ✓`); setOpen(null); onSent(); setTimeout(() => setOk(""), 4000); }} />;
  if (!tpls.length && !ok) return null;
  const s = q.trim().toLowerCase();
  const shown = tpls.filter((t) => !s || (t.code || "").startsWith(s) || t.name.toLowerCase().includes(s) || (t.category?.name || "").toLowerCase().includes(s));
  return (
    <section data-testid="agent-site-forms">
      <h2 className="font-display text-lg font-extrabold mb-2">Rapports du site</h2>
      {ok && <div data-testid="form-ok" className="text-sm rounded-md px-3 py-2 mb-2 bg-emerald-50 text-emerald-800">{ok}</div>}
      {tpls.length > 0 && <div className="relative mb-2"><Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code (ex. 0101) ou nom du rapport" inputMode="search" className={`${ipt} pl-9`} data-testid="form-search" /></div>}
      <div className="grid grid-cols-2 gap-2">
        {shown.map((t) => (
          <button key={t.id} data-testid={`form-open-${t.id}`} onClick={() => setOpen(t)} className="bg-white border border-slate-200 rounded-xl p-3 text-left hover:border-sky-400 transition-colors" style={t.category ? { borderLeft: `5px solid ${t.category.color}` } : undefined}>
            <div className="flex items-center gap-1.5 mb-1">{t.code ? <span className="font-mono text-[11px] font-extrabold rounded px-1.5 py-0.5 text-white" style={{ background: t.category?.color || "#64748B" }} data-testid={`form-code-${t.id}`}>{t.code}</span> : <ClipboardCheck className="h-5 w-5 text-[#C9962E]" />}
              {t.category && <span className="text-[10px] uppercase tracking-widest text-slate-500 truncate">{t.category.name}</span>}</div>
            <div className="text-sm font-bold leading-tight">{t.name}</div>
            <div className="text-[11px] text-slate-500 truncate">{t.sites.map((x) => x.name).join(", ")}</div>
          </button>
        ))}
        {shown.length === 0 && <p className="col-span-2 text-sm text-slate-500 text-center py-4" data-testid="form-search-empty">Aucun rapport ne correspond.</p>}
      </div>
    </section>
  );
}
